/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Check } from "lucide-react";
import GoalForm from "@/components/GoalForm";

const CATEGORIES = [
  { key: "probation", label: "Probation", color: "#4A5568" },
  { key: "health", label: "Health", color: "#9C6644" },
  { key: "whanau", label: "Whānau", color: "#8B3A33" },
  { key: "mahi", label: "Mahi · work", color: "#2B4522" },
  { key: "identity", label: "Identity", color: "#D4A373" },
  { key: "other", label: "Other", color: "#6B705C" },
];

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: "", description: "", category: "other", due_date: "" });
  const [filter, setFilter] = useState("open");

  const load = useCallback(
    () => api.get("/goals").then((r) => setGoals(r.data || [])),
    []
  );
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft.title.trim()) return;
    try {
      await api.post("/goals", {
        ...draft,
        due_date: draft.due_date || null,
        description: draft.description || null,
      });
      setDraft({ title: "", description: "", category: "other", due_date: "" });
      setAdding(false);
      load();
      toast.success("Whāinga whakatakoto · goal set");
    } catch {
      toast.error("Could not save");
    }
  };

  const toggle = async (g) => {
    await api.patch(`/goals/${g.id}`, { completed: !g.completed });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/goals/${id}`);
    load();
  };

  const visible = goals.filter((g) => (filter === "open" ? !g.completed : g.completed));

  return (
    <div className="space-y-7 animate-fadeup">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-2">Whāinga · goals</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-charcoal mb-2">What's on the path</h1>
        <p className="text-charcoal/70 max-w-2xl">
          Big or small. Your way. Probation appointments, parenting time, mahi callbacks, identity work — anything that keeps you moving forward.
        </p>
      </header>

      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-muka rounded-full p-1 border border-border">
          {[
            { k: "open", label: "Kei runga · open" },
            { k: "done", label: "Kua oti · done" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              data-testid={`goals-filter-${t.k}`}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                filter === t.k ? "bg-ngahere text-muka" : "text-charcoal/70"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setAdding(true)}
          data-testid="goals-add-button"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ngahere text-muka hover:bg-ngahere/90 text-sm"
        >
          <Plus size={16} /> add a whāinga
        </button>
      </div>

      {adding && (
        <GoalForm
          draft={draft}
          setDraft={setDraft}
          onCancel={() => setAdding(false)}
          onSave={save}
        />
      )}

      <div className="space-y-2">
        {visible.length === 0 && (
          <div className="text-center py-12 text-muted-foreground italic font-serif text-lg">
            {filter === "open" ? "Nothing here yet — add one when you're ready." : "Nothing finished yet."}
          </div>
        )}
        {visible.map((g) => {
          const cat = CATEGORIES.find((c) => c.key === g.category) || CATEGORIES[5];
          return (
            <div
              key={g.id}
              className="bg-muka rounded-2xl p-4 sm:p-5 border border-border flex items-start gap-3 group"
              data-testid={`goal-${g.id}`}
            >
              <button
                onClick={() => toggle(g)}
                data-testid={`goal-toggle-${g.id}`}
                className={`mt-1 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                  g.completed ? "bg-ngahere border-ngahere" : "border-border hover:border-ngahere"
                }`}
                aria-label="Toggle complete"
              >
                {g.completed && <Check size={14} className="text-muka" />}
              </button>
              <div className="flex-1">
                <div className={`font-medium ${g.completed ? "line-through text-muted-foreground" : "text-charcoal"}`}>
                  {g.title}
                </div>
                {g.description && (
                  <p className="text-sm text-charcoal/70 mt-1">{g.description}</p>
                )}
                <div className="flex items-center gap-3 mt-2 text-[10px] uppercase tracking-[0.15em]">
                  <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: `${cat.color}22`, color: cat.color }}>
                    {cat.label}
                  </span>
                  {g.due_date && <span className="text-muted-foreground">due {g.due_date}</span>}
                </div>
              </div>
              <button
                onClick={() => remove(g.id)}
                data-testid={`goal-remove-${g.id}`}
                className="text-muted-foreground hover:text-kokowai opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove goal"
              >
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}